import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Box,
    Button,
    Text,
    Flex,
    VisuallyHidden,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Menu, MenuButton, MenuList, MenuItem} from '@chakra-ui/react'

import {MENU_COMPONENT_GUIDE} from '@app/constants'

import useMultiSite from '@app/hooks/use-multi-site'
import {useLocation} from 'react-router-dom'
import {buildUrlWithLocale} from '@app/utils/url'

// Icons
import {ChevronDownIcon, ChevronUpIcon} from '@app/components/icons'

import LocaleText from '../locale-text'
import {useIntl} from 'react-intl'

/**
 * LocaleSelector component provides a dropdown menu for selecting the locale.
 * It utilizes Chakra UI's Menu components to display a list of supported locales,
 * allowing users to switch the language of the application.
 *
 * @param {Object} props - The component props.
 * @param {string} props.selectedLocale - The currently selected locale.
 * @param {string[]} props.locales - An array of all supported locale IDs.
 * @param {Function} props.onSelect - Callback function called when a locale is selected.
 * @returns {JSX.Element} The rendered component.
 */
const LocaleSelector = ({selectedLocale = '', locales = [], onSelect = () => {}}) => {
    const intl = useIntl()
    const location = useLocation()
    const {site, locale: currentLocale} = useMultiSite()

    return (
        <Menu placement="top-end" variant="footerLocaleMenu">
            {({isOpen}) => (
                <>
                    <MenuButton
                        as={Button}
                        textStyle={'bodyBase2'}
                        p="12px 0"
                        whiteSpace={'normal'}
                        aria-label={intl.formatMessage(MENU_COMPONENT_GUIDE)}
                    >
                        <Flex alignItems="center" justifyContent="center">
                            <Text as="span">
                                {intl.formatMessage({
                                    id: 'localeSelector.language',
                                    defaultMessage: 'Language:'
                                })}{' '}
                                <LocaleText shortCode={selectedLocale} />
                            </Text>
                            {isOpen ? (
                                <ChevronUpIcon boxSize={6} color="grey" aria-hidden="true" />
                            ) : (
                                <ChevronDownIcon boxSize={6} color="grey" aria-hidden="true" />
                            )}
                        </Flex>
                    </MenuButton>
                    <MenuList>
                        <VisuallyHidden>
                            <Text>
                                {intl.formatMessage({
                                    defaultMessage: 'Language selector',
                                    id: 'locale_selector.title'
                                })}
                            </Text>
                        </VisuallyHidden>
                        <Box as="ul">
                            {locales
                                .filter((locale) => locale?.id !== selectedLocale)
                                .map((localeObject) => {
                                    const locale = localeObject.id
                                    const localeAlias = localeObject.alias
                                    return (
                                        <Box
                                            key={localeAlias}
                                            as="li"
                                            textAlign="center"
                                            listStyleType="none"
                                        >
                                            <MenuItem
                                                as={Link}
                                                lang={locale}
                                                href={buildUrlWithLocale(
                                                    location,
                                                    site,
                                                    currentLocale,
                                                    localeObject
                                                )}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    onSelect(locale)
                                                }}
                                                icon={null}
                                            >
                                                <LocaleText shortCode={locale} />
                                            </MenuItem>
                                        </Box>
                                    )
                                })}
                        </Box>
                    </MenuList>
                </>
            )}
        </Menu>
    )
}

LocaleSelector.displayName = 'LocaleSelector'

LocaleSelector.propTypes = {
    /**
     * A complete list of all the locales supported. This array must have content.
     */
    locales: PropTypes.array.isRequired,
    /**
     * The current locales shortcode.
     */
    selectedLocale: PropTypes.string.isRequired,
    /**
     * Function called when a locale is selected.
     */
    onSelect: PropTypes.func
}

export default LocaleSelector
